import { ApiError } from "@/shared/lib/api/http";

export type FinanceGuardCode =
  | "SETUP_REQUIRED"
  | "ACCOUNT_BINDING_REQUIRED"
  | "INVALID_ACCOUNT_BRANCH"
  | "FORBIDDEN_SCOPE"
  | "INVALID_SCOPE"
  | "ACCOUNT_LOCKED_HAS_TRANSACTIONS";

export type FinanceGuardErrorDetails = {
  code: FinanceGuardCode | null;
  message: string | null;
  formErrors: string[];
  fieldErrors: Record<string, string[]>;
};

const KNOWN_FINANCE_GUARD_CODES: FinanceGuardCode[] = [
  "SETUP_REQUIRED",
  "ACCOUNT_BINDING_REQUIRED",
  "INVALID_ACCOUNT_BRANCH",
  "FORBIDDEN_SCOPE",
  "INVALID_SCOPE",
  "ACCOUNT_LOCKED_HAS_TRANSACTIONS",
];

function normalizeIssueArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is string => typeof entry === "string" && entry.trim().length > 0);
}

function normalizeFieldErrors(value: unknown): Record<string, string[]> {
  if (!value || typeof value !== "object") return {};

  const entries = Object.entries(value as Record<string, unknown>).map(([key, fieldValue]) => [
    key,
    normalizeIssueArray(fieldValue),
  ]);

  return Object.fromEntries(entries.filter(([, messages]) => messages.length > 0));
}

function extractCodeFromText(value: string | null | undefined): FinanceGuardCode | null {
  if (!value) return null;

  for (const code of KNOWN_FINANCE_GUARD_CODES) {
    if (value === code || value.includes(code)) {
      return code;
    }
  }

  return null;
}

export function getFinanceGuardErrorDetails(error: unknown): FinanceGuardErrorDetails | null {
  if (!(error instanceof ApiError)) return null;

  const formErrors = normalizeIssueArray(error.issues?.formErrors);
  const fieldErrors = normalizeFieldErrors(error.issues?.fieldErrors);
  const codeFromPayload = extractCodeFromText(error.code);
  const codeFromMessage = extractCodeFromText(error.message);
  const codeFromFormErrors = formErrors.map(extractCodeFromText).find((code): code is FinanceGuardCode => code !== null) ?? null;
  const codeFromFieldErrors =
    Object.values(fieldErrors)
      .flat()
      .map(extractCodeFromText)
      .find((code): code is FinanceGuardCode => code !== null) ?? null;

  return {
    code: codeFromPayload ?? codeFromMessage ?? codeFromFormErrors ?? codeFromFieldErrors ?? null,
    message: typeof error.message === "string" && error.message.trim().length > 0 ? error.message.trim() : null,
    formErrors,
    fieldErrors,
  };
}

function collectCodesFromText(value: string | null | undefined): FinanceGuardCode[] {
  if (!value) return [];
  return KNOWN_FINANCE_GUARD_CODES.filter((code) => value === code || value.includes(code));
}

export function collectFinanceGuardCodes(error: unknown): FinanceGuardCode[] {
  if (!(error instanceof ApiError)) return [];

  const result = new Set<FinanceGuardCode>();
  const addFromText = (value: string | null | undefined) => {
    for (const code of collectCodesFromText(value)) result.add(code);
  };

  addFromText(error.code);
  addFromText(error.message);
  for (const message of normalizeIssueArray(error.issues?.formErrors)) addFromText(message);
  for (const messages of Object.values(normalizeFieldErrors(error.issues?.fieldErrors))) {
    for (const message of messages) addFromText(message);
  }

  return [...result];
}

export function getFirstFinanceFieldError(details: FinanceGuardErrorDetails | null): string | null {
  if (!details) return null;

  for (const messages of Object.values(details.fieldErrors)) {
    const firstMessage = messages.find((message) => message.trim().length > 0);
    if (firstMessage) return firstMessage;
  }

  return null;
}

export function getFirstFinanceFormError(details: FinanceGuardErrorDetails | null): string | null {
  if (!details) return null;
  return details.formErrors.find((message) => message.trim().length > 0) ?? null;
}
